import React from 'react';
import { Box, Grid, TextField, Theme, useTheme } from '@mui/material';
import { BodyText, Heading } from '../../shared/typography/PrimaryTypography';
import { ReactComponent as SearchIcon } from '../../../assets/icons/Search.svg';
import QuickLinksBar, { QuickLink } from './QuickLinksBar';
import { Resource } from '@esg/esg-global-types';
import ResourceCategorySelect from '../../shared/input/select/ResourceCategorySelect';
import BasicSelect from '../../shared/input/select/BasicSelect';
import { ResourceFilterContext } from '../../../views/resource_centre/ResourceCentreHome';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

interface ResourceCentreContextProps {
  resources: Array<Resource>;
  filters_context: ResourceFilterContext;
  handleFiltersChange: (new_filters: ResourceFilterContext) => void;
}

/**
 * A list of inputs that defines the context of Resource Centre Resources.
 * @param {Array<Resource>} resources List of existing resources from master list to use for input options.
 * @param {ResourceFilterContext} filters_context Current filter context from parent for controlled input values.
 * @param {(new_filters: ResourceFilterContext): void} handleFiltersChange Update filters in parent from inputs.
 * @returns {JSX.Element}
 */
const ResourceCentreContext = ({
  resources,
  filters_context,
  handleFiltersChange
}: ResourceCentreContextProps) => {
  const theme: Theme = useTheme();
  const quick_links: Array<QuickLink> = [
    {
      label: 'All Resources',
      compound_filters: {
        categories: [],
        tags: [],
        types: [],
        text: ''
      }
    }
  ];

  const available_tags: Set<string> = new Set(
    resources.reduce((current_tags: Array<string>, next_resource: Resource) => {
      return [
        ...current_tags,
        ...(next_resource.tags ? next_resource.tags.map((tag: string) => tag) : [])
      ];
    }, [])
  );

  const available_types: Set<string> = new Set(
    resources.reduce((current_types: Array<string>, next_resource: Resource) => {
      return [...current_types, ...(next_resource.type ? [next_resource.type] : [])];
    }, [])
  );

  const handleUpdateSearchFilter = (search: string): void => {
    handleFiltersChange({
      ...filters_context,
      text: search
    });
  };

  const handleUpdateCategoriesFilter = (new_categories: Array<string>): void => {
    handleFiltersChange({
      ...filters_context,
      categories: new_categories
    });
  };

  const handleUpdateTagsFilter = (new_tags: Array<string>): void => {
    handleFiltersChange({
      ...filters_context,
      tags: new_tags
    });
  };

  const handleUpdateTypesFilter = (new_types: Array<string>): void => {
    handleFiltersChange({
      ...filters_context,
      types: new_types
    });
  };

  return (
    <Box
      sx={{
        textAlign: 'start',
        my: 3,
        mx: -3,
        flexGrow: 1,
        boxSizing: 'border-box',
        borderBottom: `1px solid ${theme.palette.teal.dark}`
      }}
    >
      <Grid container spacing={2} sx={{ px: 4 }}>
        <Grid size={9} alignSelf={'center'}>
          <Heading weight="Medium">Explore</Heading>
        </Grid>
        <Grid size={12} alignSelf={'center'} display={'flex'}>
          <BodyText>
            Dive into Syntiro’s Resource Centre to get access to the latest standards, research,
            platform training modules and more to support your sustainability journey.
          </BodyText>
        </Grid>
        <Grid size={12}>
          <Grid
            container
            spacing={3}
            sx={{ alignItems: 'flex-end', justifyContent: 'center', flexGrow: 1, mb: 4 }}
          >
            <Grid size={4}>
              <TextField
                fullWidth
                variant="standard"
                placeholder="Search..."
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon height={15} style={{ marginRight: 5 }} />
                  }
                }}
                value={filters_context.text}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleUpdateSearchFilter(event.target.value)
                }
              />
            </Grid>
            <Grid size={2.6}>
              <ResourceCategorySelect
                input_label="Categories"
                multi_select
                tag_limit={0}
                selected_options={filters_context.categories}
                disable_selected_tags
                handleChangeCategories={(categories: string | Array<string> | null): void => {
                  if (!categories) handleUpdateCategoriesFilter([]);
                  if (Array.isArray(categories)) handleUpdateCategoriesFilter(categories);
                  return;
                }}
              />
            </Grid>
            <Grid size={2.6}>
              <BasicSelect
                input_label="Tags"
                multi_select
                tag_limit={0}
                options={[...available_tags]}
                selected_options={filters_context.tags}
                input_icon={<StyleOutlinedIcon />}
                disable_selected_tags
                handleChangeSelection={(tags: string | Array<string> | null): void => {
                  if (!tags) handleUpdateTagsFilter([]);
                  if (Array.isArray(tags)) handleUpdateTagsFilter(tags);
                  return;
                }}
              />
            </Grid>
            <Grid size={2.6}>
              <BasicSelect
                input_label="Type"
                multi_select
                tag_limit={0}
                options={[...available_types]}
                selected_options={filters_context.types}
                input_icon={<DescriptionOutlinedIcon />}
                disable_selected_tags
                handleChangeSelection={(types: string | Array<string> | null): void => {
                  if (!types) handleUpdateTypesFilter([]);
                  if (Array.isArray(types)) handleUpdateTypesFilter(types);
                  return;
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <QuickLinksBar quick_links={quick_links} handleFiltersChange={handleFiltersChange} />
    </Box>
  );
};

export default ResourceCentreContext;
